import React, { useCallback, useEffect, useRef, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { useTenantContext } from '../../../tenant/TenantContext';
import { useNavigate } from 'react-router-dom';
import { useFilterContext } from '../infrastructure/FilterContext';
import { useDebouncedEffect } from '../../../common/utils/useDemouncedEffect';
import { useComercialContext } from '../../../comercial/ComercialContext';
import { fetchSuggestions } from '../infrastructure/CatalogService';
import { GtmService } from '../../../infrastructure/analytics/GtmService';

import {
    SearchWrapper,
    SearchContainer,
    SearchInput,
    SearchIconWrapper,
    SuggestionsDropdown,
    SuggestionItem,
    SuggestionThumb,
    SuggestionName,
} from './filter-panel/ProductSearchFilter.styles';

export const ProductSearchFilter = () => {
    useTenantContext();
    const { updateFilters, filters } = useFilterContext();
    const { actingAsCliente } = useComercialContext();
    const [searchString, setSearchString] = useState(filters?.search || '');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const containerRef = useRef(null);
    const skipNextSuggestionRef = useRef(false);
    const navigate = useNavigate();

    const searchProduct = useCallback(
        (searchTerm) => {
            // The filter update below propagates back into searchString, which would re-trigger the
            // debounced fetch and reopen the dropdown. Suppress that one fetch.
            skipNextSuggestionRef.current = true;
            setShowSuggestions(false);
            setSuggestions([]);
            const trimmed = (searchTerm ?? '').trim();
            GtmService.logSearch(trimmed);
            updateFilters(trimmed ? { search: trimmed } : {});
            // URL final en un solo salto. Antes: push de /articulos SIN query + replace con ?se=...
            // en un efecto aparte — la entrada intermedia dejaba busquedas viejas vivas en el
            // historial y podian resucitar al tocar despues una categoria.
            navigate(trimmed ? `/articulos?se=${encodeURIComponent(trimmed)}` : '/articulos');
        },
        [navigate, updateFilters]
    );

    // Al pulsar una sugerencia vamos DIRECTOS a la ficha por codigo (la sugerencia ya lo trae), en vez
    // de lanzar una busqueda por el nombre completo: los nombres con parentesis/barras (vacunas, "(Jeringas
    // s/c)") podian no encontrarse y el cliente creia que no teniamos el producto. Asi siempre acierta.
    const goToProduct = useCallback(
        (code) => {
            skipNextSuggestionRef.current = true;
            setShowSuggestions(false);
            setSuggestions([]);
            navigate(`/articulos/${encodeURIComponent(code)}`);
        },
        [navigate]
    );

    const handleChange = (event) => {
        event.preventDefault();
        const value = event.target.value;
        // A real keystroke always means the user wants suggestions — clear any pending suppression.
        skipNextSuggestionRef.current = false;
        setSearchString(value);
        if (value.length < 3) {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            searchProduct(searchString);
        }
        if (event.key === 'Escape') {
            setShowSuggestions(false);
        }
    };

    useDebouncedEffect(() => {
        if (skipNextSuggestionRef.current) {
            skipNextSuggestionRef.current = false;
            return;
        }
        if (searchString.length < 3) return;
        const actingAs = actingAsCliente?.numCliente ?? null;
        fetchSuggestions(searchString, actingAs).then((result) => {
            if (result.state === 'success' && Array.isArray(result.value)) {
                setSuggestions(result.value);
                setShowSuggestions(result.value.length > 0);
            }
        });
    }, [searchString, actingAsCliente], 250);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // La caja solo se re-sincroniza cuando CAMBIA la busqueda activa (quitar el chip,
    // atras/adelante, nueva busqueda). Antes cualquier cambio de filtros (elegir una
    // categoria, tocar el panel) machacaba lo que estuvieras escribiendo sin buscar,
    // y parecia que "volvia" la busqueda anterior.
    const prevSearchRef = useRef(filters?.search || '');
    useEffect(() => {
        const activa = filters?.search || '';
        if (activa !== prevSearchRef.current) {
            prevSearchRef.current = activa;
            setSearchString(activa);
        }
    }, [filters]);

    return (
        <div className="w-100 d-flex flex-column gap-2 align-items-center" ref={containerRef}>
            <SearchWrapper>
                <SearchContainer>
                    <SearchInput
                        placeholder="Busca aquí tus productos"
                        value={searchString}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        type="text"
                    />
                    <SearchIconWrapper onClick={() => searchProduct(searchString)}>
                        <SearchIcon />
                    </SearchIconWrapper>
                </SearchContainer>
                {showSuggestions && suggestions.length > 0 && (
                    <SuggestionsDropdown>
                        {suggestions.map((s) => (
                            <SuggestionItem
                                key={s.codigo}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    GtmService.logSelectItem({ code: s.codigo, name: s.nombre }, 'sugerencias');
                                    goToProduct(s.codigo);
                                }}
                            >
                                {s.imagenPrincipal && (
                                    <SuggestionThumb src={s.imagenPrincipal} alt="" />
                                )}
                                <SuggestionName>{s.nombre}</SuggestionName>
                            </SuggestionItem>
                        ))}
                    </SuggestionsDropdown>
                )}
            </SearchWrapper>
        </div>
    );
};
